/**
 * Keeps the signed-in staff member's permissions in step with the server
 * without a reload (R15). Started once in main.ts:
 *
 *  1. socket `permissions:changed` → refetch (an event during a refetch
 *     queues exactly one more);
 *  2. socket `session:revoked`    → sign out locally, go to sign-in, notice;
 *  3. X-Permissions-Version header differs from the store → refetch
 *     (debounced 1 s, single flight);
 *  4. a 403 PERMISSION_DENIED / MODULE_DISABLED → refetch if the last check
 *     is older than 10 s;
 *  5. window focus / tab visible / socket reconnect → refetch if the last
 *     check is older than 60 s.
 *
 * After a refetch, if the open page is no longer allowed, the app replaces it
 * with the landing page, shows the "your access changed" notice and fires the
 * window event `rbac:access-changed`.
 *
 * Two more things follow the session here, for every route (the dashboard
 * layout, full-page routes like /tdrive, /ai-chatbot and /org-chart, editors):
 *  - the socket is connected for any signed-in staff member and reconnected
 *    when another user signs in, so `permissions:changed` / `session:revoked`
 *    always arrive (L2);
 *  - `permissionsNarrowed` counts live narrowings of the same user's access
 *    (a grant removed or its scope lowered). The dashboard <router-view> is
 *    keyed on it, so the open page remounts and refetches under the new
 *    scope (M1). Widening never bumps it, so open forms survive.
 */
import { ref, watch } from 'vue';
import type { Router } from 'vue-router';
import { setPermissionsSignalListener, setSessionEndedHandler } from '../services/api';
import { connectSocket, disconnectSocket, onPersistentSocketEvent, SOCKET_EVENTS } from '../services/socket';
import { useAuthStore } from '../stores/auth';
import { isNarrowing } from './can';
import { isRouteAllowed, signInRouteName } from './guard';
import { landingRouteName } from './navigation';
import { showRbacNotice } from './notices';

export const VERSION_REFRESH_DEBOUNCE_MS = 1000;
export const FOCUS_REFRESH_AFTER_MS = 60_000;
export const DENIED_REFRESH_AFTER_MS = 10_000;
export const ACCESS_CHANGED_EVENT = 'rbac:access-changed';

/** Live narrowings of the signed-in user's access (key of the dashboard <router-view>). */
export const permissionsNarrowed = ref(0);

let routerRef: Router | null = null;
let running: Promise<void> | null = null;
let again = false;
let versionTimer: ReturnType<typeof setTimeout> | null = null;
let pendingVersion: string | null = null;

function isStale(maxAgeMs: number): boolean {
  const last = useAuthStore().lastPermissionsCheckAt;
  return last === null || Date.now() - last > maxAgeMs;
}

/**
 * Refetch the permissions, then re-check the open page.
 * `join` (default) shares a refetch that is already running; `again` also
 * queues one more after it (the change may have happened after it started).
 */
export function requestPermissionRefresh(reason: string, mode: 'join' | 'again' = 'join'): Promise<void> {
  if (running) {
    if (mode === 'again') again = true;
    return running;
  }
  running = (async () => {
    try {
      const auth = useAuthStore();
      do {
        again = false;
        await auth.refreshPermissions({ reason });
      } while (again);
      if (routerRef) await enforceCurrentRoute(routerRef);
    } finally {
      running = null;
    }
  })();
  return running;
}

/** Leave the open page if the current permissions no longer allow it. Resolves to "moved". */
export async function enforceCurrentRoute(router: Router): Promise<boolean> {
  const auth = useAuthStore();
  if (!auth.isAuthenticated || auth.principal !== 'staff') return false;
  const current = router.currentRoute.value;
  if (!current.matched.length || isRouteAllowed(current, auth.permissionSnapshot)) return false;
  // Leave quietly: employees are not told their access changed (owner decision).
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ACCESS_CHANGED_EVENT, { detail: { from: current.fullPath } }));
  }
  await router.replace({ name: landingRouteName(auth.permissionSnapshot) }).catch(() => {});
  return true;
}

/** Called for every response carrying X-Permissions-Version. A new value → one debounced refetch. */
export function notePermissionsVersionHeader(version: string | null | undefined): void {
  if (!version) return;
  const auth = useAuthStore();
  if (auth.principal !== 'staff') return;
  if (version === auth.permissionsVersion) {
    pendingVersion = null;
    return;
  }
  if (version === pendingVersion && versionTimer) return;
  pendingVersion = version;
  if (versionTimer) clearTimeout(versionTimer);
  versionTimer = setTimeout(() => {
    versionTimer = null;
    const target = pendingVersion;
    pendingVersion = null;
    if (target && target !== useAuthStore().permissionsVersion) void requestPermissionRefresh('version-header');
  }, VERSION_REFRESH_DEBOUNCE_MS);
}

/** Called for a 403 PERMISSION_DENIED / MODULE_DISABLED answer. */
export function notePermissionDenied(): void {
  const auth = useAuthStore();
  if (auth.principal !== 'staff' || !isStale(DENIED_REFRESH_AFTER_MS)) return;
  void requestPermissionRefresh('denied');
}

function refreshIfStale(reason: string): void {
  const auth = useAuthStore();
  if (auth.principal !== 'staff' || !isStale(FOCUS_REFRESH_AFTER_MS)) return;
  void requestPermissionRefresh(reason);
}

async function handleSessionRevoked(router: Router): Promise<void> {
  const auth = useAuthStore();
  const principal = auth.principal;
  if (!auth.isAuthenticated) return;
  auth.signOutLocally();
  showRbacNotice('sessionRevoked');
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  await router.replace({ name: signInRouteName(principal, hostname) }).catch(() => {});
}

/** Wire every refresh trigger. Returns a stop function (tests, HMR). */
export function startPermissionSync(router: Router): () => void {
  routerRef = router;

  setPermissionsSignalListener((signal) => {
    if (signal.version) notePermissionsVersionHeader(signal.version);
    if (signal.denied) notePermissionDenied();
  });
  setSessionEndedHandler(() => useAuthStore().signOutLocally());

  const offSocket = [
    onPersistentSocketEvent<{ version?: string | null; reason?: string } | undefined>(SOCKET_EVENTS.PERMISSIONS_CHANGED, (payload) => {
      if (payload?.version && payload.version === useAuthStore().permissionsVersion) return;
      void requestPermissionRefresh('socket', 'again');
    }),
    onPersistentSocketEvent(SOCKET_EVENTS.SESSION_REVOKED, () => {
      void handleSessionRevoked(router);
    }),
    // Events missed while disconnected: catch up on reconnect.
    onPersistentSocketEvent('connect', () => refreshIfStale('reconnect')),
  ];

  const onFocus = () => refreshIfStale('focus');
  const onVisibility = () => {
    if (document.visibilityState === 'visible') refreshIfStale('visible');
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisibility);

  const auth = useAuthStore();

  // The socket follows the staff session, not a layout: connected while a
  // staff member is signed in, reconnected (re-authenticated) for another
  // user, disconnected on sign-out. It waits for the session check
  // (checkAuth may still be refreshing a persisted token), so it never
  // authenticates with a stale one.
  const stopSocketSession = watch(
    () => (auth.isInitialized && auth.isAuthenticated && auth.principal === 'staff' ? auth.user?.id ?? null : null),
    (id, previous) => {
      if (id === previous) return;
      if (previous != null) disconnectSocket();
      if (id != null) connectSocket();
    },
    { immediate: true }
  );

  // Same user, fewer or narrower grants → remount the open page (M1).
  const stopNarrowing = watch(
    () => ({ id: auth.user?.id ?? null, snapshot: auth.permissionSnapshot }),
    (next, prev) => {
      if (next.id == null || next.id !== prev.id) return;
      if (isNarrowing(prev.snapshot, next.snapshot)) permissionsNarrowed.value++;
    }
  );

  return () => {
    stopSocketSession();
    stopNarrowing();
    offSocket.forEach((off) => off());
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onVisibility);
    setPermissionsSignalListener(null);
    setSessionEndedHandler(null);
    if (routerRef === router) routerRef = null;
  };
}

/** Test helper: forget timers and in-flight state. */
export function resetPermissionSyncForTests(): void {
  if (versionTimer) clearTimeout(versionTimer);
  versionTimer = null;
  pendingVersion = null;
  running = null;
  again = false;
  routerRef = null;
}
